<template>
  <div class="table-content">
    <div class="table-wrap">
      <div class="table-title-div">
        <p class="table-title">Data Quality Scorecard - {{ datasetLabel }}</p>
        <div class="qaqc-scorecard-header-actions">
          <div class="csv-btn" @click="downloadTableCSV">
            <v-img class="pointer" :src="documentDownload" alt="csv download"></v-img>
          </div>
          <v-btn
            icon
            variant="text"
            size="small"
            class="qaqc-scorecard-collapse-btn"
            :aria-label="isCollapsed ? 'Expand' : 'Collapse'"
            @click="isCollapsed = !isCollapsed"
          >
            <v-icon>{{ isCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
          </v-btn>
        </div>
      </div>
      <div v-show="!isCollapsed" class="qaqc-table-content">
      <div class="qaqc-scorecard-summary-bar" v-if="summary">
        <div class="qaqc-scorecard-summary-card qaqc-scorecard-summary-overall">
          <div class="qaqc-scorecard-summary-icon-wrap">
            <v-img :src="icons['score']" contain class="qaqc-scorecard-summary-icon-img" />
          </div>
          <span class="qaqc-scorecard-summary-label">Overall Pass Rate</span>
          <span class="qaqc-scorecard-summary-value qaqc-scorecard-summary-value-orange">{{ summary.overallPassRate }}</span>
        </div>
        <div class="qaqc-scorecard-summary-card qaqc-scorecard-summary-passed">
          <div class="qaqc-scorecard-summary-icon-wrap">
            <v-img :src="icons['passed']" contain class="qaqc-scorecard-summary-icon-img" />
          </div>
          <span class="qaqc-scorecard-summary-label">Passed QC</span>
          <span class="qaqc-scorecard-summary-value">{{ summary.passedQc }}</span>
        </div>
        <div class="qaqc-scorecard-summary-card qaqc-scorecard-summary-pending">
          <div class="qaqc-scorecard-summary-icon-wrap">
            <v-img :src="icons['pending']" contain class="qaqc-scorecard-summary-icon-img" />
          </div>
          <span class="qaqc-scorecard-summary-label">Pending QC</span>
          <span class="qaqc-scorecard-summary-value">{{ summary.pendingQc }}</span>
        </div>
        <div class="qaqc-scorecard-summary-card qaqc-scorecard-summary-failed">
          <div class="qaqc-scorecard-summary-icon-wrap">
            <v-img :src="icons['failed']" contain class="qaqc-scorecard-summary-icon-img" />
          </div>
          <span class="qaqc-scorecard-summary-label">Failed</span>
          <span class="qaqc-scorecard-summary-value">{{ summary.failed }}</span>
        </div>
      </div>
      <v-table class="test-data-table qaqc-table-wrap qaqc-scorecard-data-table">
        <thead>
          <tr class="main-header">
            <th class="sticky-header-sub search-dropdown-menu" :style="{ backgroundColor: searchDropdowns.well ? 'black !important' : 'transparent' }">
              <v-menu
                v-model="searchDropdowns.well"
                attach="body"
                :close-on-content-click="false"
                location="bottom"
                transition="scale-transition"
              >
                <template v-slot:activator="{ props: menuActivatorProps }">
                  <div class="sort-icon-title d-flex align-center" style="gap: 10px; width: 100%; justify-content: space-between;" v-bind="menuActivatorProps">
                    <div class="sort-icons-with-text d-flex align-center" style="gap: 10px;" @click.stop>
                      <div class="sort-icons">
                        <div class="img-div">
                          <img :src="sort_up" alt="sort-up" class="cursor-pointer" @click="sortColumn('well', 'asc')" />
                        </div>
                        <div class="img-div">
                          <img :src="sort_down" alt="sort-down" class="cursor-pointer" @click="sortColumn('well', 'desc')" />
                        </div>
                      </div>
                      <span @click.stop>Well</span>
                    </div>
                    <div v-if="searchDropdowns.well" class="close-icon" @click.stop="searchDropdowns.well = false">
                      <v-img :src="close_icon" alt="close-icon"></v-img>
                    </div>
                    <div v-else class="search-icon">
                      <v-img :src="magnify" alt="search-icon" width="20" height="20"></v-img>
                    </div>
                  </div>
                </template>
                <v-list class="qaqc-scorecard-search-list">
                  <v-text-field variant="solo" class="search-table" v-model="searchQuery.well" label="Search wells" single-line hide-details @click.stop>
                    <template v-slot:prepend>
                      <v-img :src="magnify_black" class="ml-2 mr-2" width="16" height="16"></v-img>
                    </template>
                  </v-text-field>
                  <v-list-item v-for="option in getFilteredOptions('well')" :key="'w-' + option" class="list-items qaqc-scorecard-filter-item" @click.stop>
                    <div class="data-list-content">
                      <input type="checkbox" :checked="isOptionSelected('well', option)" @change="toggleSelection('well', option)" @click.stop class="custom-checkbox" />
                      <span class="option-text">{{ option }}</span>
                    </div>
                  </v-list-item>
                  <v-list-item v-if="getFilteredOptions('well').length === 0" disabled>
                    <v-list-item-title style="font-size: 13px;">No data found</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="getSelectedOptions('well').length > 0" class="qaqc-scorecard-filter-item" @click.stop="clearColumnFilters('well')">
                    <v-list-item-title style="font-size: 13px; color: #1976d2;">Clear Filters ({{ getSelectedOptions('well').length }})</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </th>
            <th class="sticky-header-sub search-dropdown-menu" :style="{ backgroundColor: searchDropdowns.samples ? 'black !important' : 'transparent' }">
              <v-menu
                v-model="searchDropdowns.samples"
                :attach="false"
                :close-on-content-click="false"
                location="bottom"
                transition="scale-transition"
              >
                <template v-slot:activator="{ props: menuActivatorProps }">
                  <div class="sort-icon-title d-flex align-center" style="gap: 10px; width: 100%; justify-content: space-between;" v-bind="menuActivatorProps">
                    <div class="sort-icons-with-text d-flex align-center" style="gap: 10px;" @click.stop>
                      <div class="sort-icons">
                        <div class="img-div">
                          <img :src="sort_up" alt="sort-up" class="cursor-pointer" @click="sortColumn('samples', 'asc')" />
                        </div>
                        <div class="img-div">
                          <img :src="sort_down" alt="sort-down" class="cursor-pointer" @click="sortColumn('samples', 'desc')" />
                        </div>
                      </div>
                      <span @click.stop>Samples</span>
                    </div>
                    <div v-if="searchDropdowns.samples" class="close-icon" @click.stop="searchDropdowns.samples = false">
                      <v-img :src="close_icon" alt="close-icon"></v-img>
                    </div>
                    <div v-else class="search-icon">
                      <v-img :src="magnify" alt="search-icon" width="20" height="20"></v-img>
                    </div>
                  </div>
                </template>
                <v-list class="qaqc-scorecard-search-list">
                  <v-text-field variant="solo" class="search-table" v-model="searchQuery.samples" label="Search samples" single-line hide-details @click.stop>
                    <template v-slot:prepend>
                      <v-img :src="magnify_black" class="ml-2" width="24" height="24"></v-img>
                    </template>
                  </v-text-field>
                  <v-list-item v-for="option in getFilteredOptions('samples')" :key="'s-' + option" class="list-items qaqc-scorecard-filter-item" @click.stop>
                    <div class="data-list-content">
                      <input type="checkbox" :checked="isOptionSelected('samples', option)" @change="toggleSelection('samples', option)" @click.stop class="custom-checkbox" />
                      <span class="option-text">{{ option }}</span>
                    </div>
                  </v-list-item>
                  <v-list-item v-if="getFilteredOptions('samples').length === 0" disabled>
                    <v-list-item-title style="font-size: 13px;">No data found</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="getSelectedOptions('samples').length > 0" class="qaqc-scorecard-filter-item" @click.stop="clearColumnFilters('samples')">
                    <v-list-item-title style="font-size: 13px; color: #1976d2;">Clear Filters ({{ getSelectedOptions('samples').length }})</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </th>
            <th class="sticky-header-sub search-dropdown-menu" :style="{ backgroundColor: searchDropdowns.passRate ? 'black !important' : 'transparent' }">
              <v-menu
                v-model="searchDropdowns.passRate"
                attach="body"
                :close-on-content-click="false"
                location="bottom"
                transition="scale-transition"
              >
                <template v-slot:activator="{ props: menuActivatorProps }">
                  <div class="sort-icon-title d-flex align-center" style="gap: 10px; width: 100%; justify-content: space-between;" v-bind="menuActivatorProps">
                    <div class="sort-icons-with-text d-flex align-center" style="gap: 10px;" @click.stop>
                      <div class="sort-icons">
                        <div class="img-div">
                          <img :src="sort_up" alt="sort-up" class="cursor-pointer" @click="sortColumn('passRate', 'asc')" />
                        </div>
                        <div class="img-div">
                          <img :src="sort_down" alt="sort-down" class="cursor-pointer" @click="sortColumn('passRate', 'desc')" />
                        </div>
                      </div>
                      <span @click.stop>Pass Rate</span>
                    </div>
                    <div v-if="searchDropdowns.passRate" class="close-icon" @click.stop="searchDropdowns.passRate = false">
                      <v-img :src="close_icon" alt="close-icon"></v-img>
                    </div>
                    <div v-else class="search-icon">
                      <v-img :src="magnify" alt="search-icon" width="20" height="20"></v-img>
                    </div>
                  </div>
                </template>
                <v-list class="qaqc-scorecard-search-list">
                  <v-text-field variant="solo" class="search-table" v-model="searchQuery.passRate" label="Search pass rate" single-line hide-details @click.stop>
                    <template v-slot:prepend>
                      <v-img :src="magnify_black" class="ml-2" width="24" height="24"></v-img>
                    </template>
                  </v-text-field>
                  <v-list-item v-for="option in getFilteredOptions('passRate')" :key="'p-' + option" class="list-items qaqc-scorecard-filter-item" @click.stop>
                    <div class="data-list-content">
                      <input type="checkbox" :checked="isOptionSelected('passRate', option)" @change="toggleSelection('passRate', option)" @click.stop class="custom-checkbox" />
                      <span class="option-text">{{ option }}</span>
                    </div>
                  </v-list-item>
                  <v-list-item v-if="getFilteredOptions('passRate').length === 0" disabled>
                    <v-list-item-title style="font-size: 13px;">No data found</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="getSelectedOptions('passRate').length > 0" class="qaqc-scorecard-filter-item" @click.stop="clearColumnFilters('passRate')">
                    <v-list-item-title style="font-size: 13px; color: #1976d2;">Clear Filters ({{ getSelectedOptions('passRate').length }})</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </th>
            <th class="sticky-header-sub search-dropdown-menu" :style="{ backgroundColor: searchDropdowns.avgQualityScore ? 'black !important' : 'transparent' }">
              <v-menu
                v-model="searchDropdowns.avgQualityScore"
                attach="body"
                :close-on-content-click="false"
                location="bottom"
                transition="scale-transition"
              >
                <template v-slot:activator="{ props: menuActivatorProps }">
                  <div class="sort-icon-title d-flex align-center" style="gap: 10px; width: 100%; justify-content: space-between;" v-bind="menuActivatorProps">
                    <div class="sort-icons-with-text d-flex align-center" style="gap: 10px;" @click.stop>
                      <div class="sort-icons">
                        <div class="img-div">
                          <img :src="sort_up" alt="sort-up" class="cursor-pointer" @click="sortColumn('avgQualityScore', 'asc')" />
                        </div>
                        <div class="img-div">
                          <img :src="sort_down" alt="sort-down" class="cursor-pointer" @click="sortColumn('avgQualityScore', 'desc')" />
                        </div>
                      </div>
                      <span @click.stop>Average Quality Score</span>
                    </div>
                    <div v-if="searchDropdowns.avgQualityScore" class="close-icon" @click.stop="searchDropdowns.avgQualityScore = false">
                      <v-img :src="close_icon" alt="close-icon"></v-img>
                    </div>
                    <div v-else class="search-icon">
                      <v-img :src="magnify" alt="search-icon" width="20" height="20"></v-img>
                    </div>
                  </div>
                </template>
                <v-list class="qaqc-scorecard-search-list">
                  <v-text-field variant="solo" class="search-table" v-model="searchQuery.avgQualityScore" label="Search avg score" single-line hide-details @click.stop>
                    <template v-slot:prepend>
                      <v-img :src="magnify_black" class="ml-2" width="24" height="24"></v-img>
                    </template>
                  </v-text-field>
                  <v-list-item v-for="option in getFilteredOptions('avgQualityScore')" :key="'a-' + option" class="list-items qaqc-scorecard-filter-item" @click.stop>
                    <div class="data-list-content">
                      <input type="checkbox" :checked="isOptionSelected('avgQualityScore', option)" @change="toggleSelection('avgQualityScore', option)" @click.stop class="custom-checkbox" />
                      <span class="option-text">{{ option }}</span>
                    </div>
                  </v-list-item>
                  <v-list-item v-if="getFilteredOptions('avgQualityScore').length === 0" disabled>
                    <v-list-item-title style="font-size: 13px;">No data found</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="getSelectedOptions('avgQualityScore').length > 0" class="qaqc-scorecard-filter-item" @click.stop="clearColumnFilters('avgQualityScore')">
                    <v-list-item-title style="font-size: 13px; color: #1976d2;">Clear Filters ({{ getSelectedOptions('avgQualityScore').length }})</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, idx) in paginatedRows" :key="idx">
            <td @click="navigateToWellDetail(row.well)">
              <span class="cursor-pointer clickable-cell well-cell no-bg">{{ row.well }}</span>
            </td>
            <td class="text-left">{{ row.samples }}</td>
            <td class="text-left">{{ row.passRate }}</td>
            <td class="text-left">{{ row.avgQualityScore }}</td>
          </tr>
        </tbody>
      </v-table>
      <QAQCPagination
        :total-items="totalItems"
        v-model:current-page="currentPage"
        v-model:items-per-page="itemsPerPage"
        item-label="Wells"
        @update:items-per-page="currentPage = 1"
      />
      </div>
    </div>
  </div>
</template>

<script>
import QAQCPagination from "../QAQCPagination.vue";

const SCORECARD_FILTER_KEYS = ["well", "samples", "passRate", "avgQualityScore"];

export default {
  name: "DataQualityScorecardTable",
  components: { QAQCPagination },
  data() {
    return {
      icons: {
        score: require("@/assets/Svg/score.svg"),
        passed: require("@/assets/Svg/passed.svg"),
        pending: require("@/assets/Svg/pending.svg"),
        failed: require("@/assets/Svg/failed.svg"),
      },
      magnify: require("@/assets/Images/MagnifyingGlass.png"),
      magnify_black: require("@/assets/Images/magnify-black.png"),
      sort_up: require("@/assets/Svg/Table/up.svg"),
      sort_down: require("@/assets/Svg/Table/down.svg"),
      close_icon: require("@/assets/Images/close-circle.png"),
      searchDropdowns: {
        well: false,
        samples: false,
        passRate: false,
        avgQualityScore: false,
      },
      searchQuery: {
        well: "",
        samples: "",
        passRate: "",
        avgQualityScore: "",
      },
      selectedFilters: {
        well: [],
        samples: [],
        passRate: [],
        avgQualityScore: [],
      },
      currentSortColumn: "",
      sortDirection: "asc",
      itemsPerPage: 10,
      currentPage: 1,
      isCollapsed: false,
      documentDownload: require("@/assets/Images/csv.svg"),
    };
  },
  props: {
    rows: { type: Array, default: () => [] },
    summary: {
      type: Object,
      default: () => ({
        overallPassRate: "0%",
        passedQc: 0,
        pendingQc: 0,
        failed: 0,
      }),
    },
    datasetLabel: { type: String, default: "CCE Dataset" },
  },
  computed: {
    filteredRows() {
      let list = [...(this.rows || [])];
      if (this.selectedFilters.well.length > 0) {
        list = list.filter((row) => this.selectedFilters.well.includes(this.getRowValue(row, "well")));
      }
      if (this.selectedFilters.samples.length > 0) {
        list = list.filter((row) => this.selectedFilters.samples.includes(this.getRowValue(row, "samples")));
      }
      if (this.selectedFilters.passRate.length > 0) {
        list = list.filter((row) => this.selectedFilters.passRate.includes(this.getRowValue(row, "passRate")));
      }
      if (this.selectedFilters.avgQualityScore.length > 0) {
        list = list.filter((row) =>
          this.selectedFilters.avgQualityScore.includes(this.getRowValue(row, "avgQualityScore"))
        );
      }
      if (this.currentSortColumn) {
        list.sort((a, b) => {
          const cmp = this.compareSortValues(a, b, this.currentSortColumn);
          return this.sortDirection === "asc" ? cmp : -cmp;
        });
      }
      return list;
    },
    totalItems() {
      return this.filteredRows.length;
    },
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.itemsPerPage, this.totalItems);
    },
    paginatedRows() {
      const list = this.filteredRows;
      return list.slice(this.startIndex, this.endIndex);
    },
  },
  watch: {
    rows() {
      this.currentPage = 1;
      this.resetColumnFilters();
    },
    selectedFilters: {
      handler() {
        this.currentPage = 1;
      },
      deep: true,
    },
  },
  methods: {
    getRowValue(row, column) {
      if (!row) return "";
      const v = row[column];
      if (v === null || v === undefined) return "";
      return String(v);
    },
    getFilteredOptions(column) {
      const list = this.rows || [];
      const allValues = list.map((row) => this.getRowValue(row, column));
      const uniqueValues = [...new Set(allValues)].filter((value) => value !== "");
      const query = (this.searchQuery[column] || "").toLowerCase();
      return uniqueValues.filter((value) => value.toLowerCase().includes(query));
    },
    isOptionSelected(column, option) {
      return this.selectedFilters[column].includes(option);
    },
    toggleSelection(column, option) {
      const prev = this.selectedFilters[column] || [];
      const index = prev.indexOf(option);
      const next = index > -1 ? prev.filter((_, i) => i !== index) : [...prev, option];
      this.selectedFilters = { ...this.selectedFilters, [column]: next };
    },
    getSelectedOptions(column) {
      return this.selectedFilters[column];
    },
    clearColumnFilters(column) {
      this.selectedFilters = { ...this.selectedFilters, [column]: [] };
      this.searchQuery = { ...this.searchQuery, [column]: "" };
    },
    resetColumnFilters() {
      const nextSel = { ...this.selectedFilters };
      const nextQ = { ...this.searchQuery };
      SCORECARD_FILTER_KEYS.forEach((key) => {
        nextSel[key] = [];
        nextQ[key] = "";
      });
      this.selectedFilters = nextSel;
      this.searchQuery = nextQ;
      this.currentSortColumn = "";
      this.sortDirection = "asc";
    },
    sortColumn(column, direction) {
      this.currentSortColumn = column;
      this.sortDirection = direction;
    },
    compareSortValues(a, b, column) {
      const va = this.getRowValue(a, column);
      const vb = this.getRowValue(b, column);
      if (column === "well") {
        return va.localeCompare(vb, undefined, { numeric: true, sensitivity: "base" });
      }
      const na = this.parseNumericSort(va);
      const nb = this.parseNumericSort(vb);
      if (na !== null && nb !== null) {
        return na - nb;
      }
      return va.localeCompare(vb, undefined, { numeric: true });
    },
    parseNumericSort(s) {
      if (s === "" || s === "-") return null;
      const n = parseFloat(String(s).replace(/%/g, "").trim());
      return Number.isFinite(n) ? n : null;
    },
    navigateToWellDetail(wellName) {
      if (wellName == null || wellName === "" || wellName === "-") return;
      this.$router.push({
        name: "QAQCDetailView",
        query: { type: "wells", well: String(wellName) },
      });
    },
    downloadTableCSV() {
      const rows = this.filteredRows;
      if (!rows || rows.length === 0) {
        return;
      }
      const safeLabel = (this.datasetLabel || "dataset").replace(/[^a-z0-9-_]/gi, "_").toLowerCase();
      const fileName = `data_quality_scorecard_${safeLabel}.csv`;
      const csvHeader = ["Well", "Samples", "Pass Rate", "Average Quality Score"];
      const csvRows = rows.map((row) => [
        String(row.well ?? ""),
        String(row.samples ?? ""),
        String(row.passRate ?? ""),
        String(row.avgQualityScore ?? ""),
      ]);
      const csvContent = [csvHeader.join(","), ...csvRows.map((row) => row.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    },
  },
};
</script>

<style scoped>

.search-table img {
  height: 12px !important;
  width: 12px !important;
}

.qaqc-scorecard-search-list {
  min-width: 182px;
  border-radius: 8px !important;
  max-height: 244px;
  padding: 8px;
  overflow-y: auto;
}
.qaqc-scorecard-filter-item {
  margin: 0 !important;
  padding: 4px 8px !important;
  min-height: 32px !important;
}
.qaqc-scorecard-data-table .sort-icon-title span {
  color: inherit;
}
.qaqc-scorecard-data-table :deep(.v-table__wrapper) {
  overflow-x: auto;
  overflow-y: visible;
}
.qaqc-scorecard-data-table :deep(thead th) {
  overflow: visible;
  vertical-align: middle;
}
</style>
